import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Clock, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const WeekendIleDeRe = () => {
  return (
    <>
      <SEO 
        title="Week-end Île de Ré | Escapade 2-3 Jours depuis Paris | Villa Nomade"
        description="Week-end réussi à l'Île de Ré : programme 2-3 jours, que faire, où dormir. À 3h de Paris en TGV. Location villa court séjour possible hors saison."
        keywords="week-end Île de Ré, escapade Ré, court séjour Île de Ré, week-end depuis Paris, 2 jours Île de Ré"
        ogImage="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1200"
        url="/weekend-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/weekend.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Calendar className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Week-end à l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Escapade ressourçante à 3h de Paris • Programme 2-3 jours optimisé
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">L'Île de Ré en Week-end - Mission Possible !</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto mb-8">
            L'<strong>Île de Ré</strong> est LA destination idéale pour un <strong>week-end ressourçant</strong> depuis Paris. 
            À seulement <strong>3h en TGV</strong> (Paris → La Rochelle), l'île offre un dépaysement total : 
            plages, villages authentiques, vélo, gastronomie. Voici notre programme optimisé pour profiter au maximum en 2-3 jours.
          </p>
        </section>

        {/* Accès */}
        <section className="mb-16 bg-blue-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">🚄 Comment Venir ?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3">Depuis Paris (Recommandé)</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>TGV Paris-La Rochelle :</strong> 3h (départ Montparnasse)</li>
                <li><strong>Location voiture :</strong> Gare La Rochelle → Île de Ré (30 min)</li>
                <li><strong>Bus :</strong> Ligne directe gare → île (économique)</li>
                <li><strong>Timing idéal :</strong> Train vendredi soir, retour dimanche soir</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-3">En Voiture</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Depuis Paris :</strong> 5h (A10 autoroute)</li>
                <li><strong>Depuis Bordeaux :</strong> 2h30</li>
                <li><strong>Depuis Nantes :</strong> 2h</li>
                <li><strong>Péage pont :</strong> 18€ (aller-retour inclus) en saison</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Programme 2 jours */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">📅 Programme Week-end 2 Jours</h2>
          
          <div className="space-y-8">
            {/* Jour 1 */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="bg-[#c17a3a] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">1</div>
                Samedi - Découverte & Détente
              </h3>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">10h00</div>
                  <div>
                    <strong>Arrivée à la Villa Nomade</strong>
                    <p className="text-gray-700">Installation, café sur la terrasse. Location vélos (possibilité de livraison à la villa).</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">11h30</div>
                  <div>
                    <strong>Vélo vers la Plage du Trousse-Chemise</strong>
                    <p className="text-gray-700">3 km à vélo (15 min). L'une des plus belles plages de l'île. Baignade et détente.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">13h30</div>
                  <div>
                    <strong>Déjeuner à Saint-Martin-de-Ré</strong>
                    <p className="text-gray-700">8 km à vélo. Déjeuner fruits de mer au port. Balade dans les fortifications Vauban (UNESCO).</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">16h00</div>
                  <div>
                    <strong>Visite du Village d'Ars-en-Ré</strong>
                    <p className="text-gray-700">Clocher noir et blanc emblématique, galeries d'art, boutiques artisanales. Glace artisanale obligatoire !</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">18h00</div>
                  <div>
                    <strong>Retour Villa - Spa de Nage</strong>
                    <p className="text-gray-700">Détente dans le spa chauffé, apéro sur la terrasse au calme.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">20h00</div>
                  <div>
                    <strong>Dîner BBQ dans le Jardin</strong>
                    <p className="text-gray-700">Courses au marché du matin : huîtres, fromages, vins locaux. Soirée tranquille sous les étoiles.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Jour 2 */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">2</div>
                Dimanche - Nature & Panoramas
              </h3>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">9h00</div>
                  <div>
                    <strong>Marché des Portes-en-Ré</strong>
                    <p className="text-gray-700">5 min à pied. Marché local authentique (produits du terroir, artisanat). Petit-déjeuner crêpes.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">11h00</div>
                  <div>
                    <strong>Phare des Baleines</strong>
                    <p className="text-gray-700">9 km à vélo (30 min). Montée au phare (257 marches), vue panoramique époustouflante à 360°. Musée inclus.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">13h00</div>
                  <div>
                    <strong>Déjeuner Plage de la Conche</strong>
                    <p className="text-gray-700">Pique-nique sur la plus grande plage de l'île (plusieurs km de sable fin). Promenade au bord de l'eau.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">15h00</div>
                  <div>
                    <strong>Visite Marais Salants</strong>
                    <p className="text-gray-700">Visite guidée avec saunier (1h30). Découverte du sel de Ré, récolte, dégustation. Expérience authentique.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">17h00</div>
                  <div>
                    <strong>Derniers Moments à la Villa</strong>
                    <p className="text-gray-700">Dernier bain dans le spa, rangement tranquille, adieux à l'île.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="font-semibold min-w-[100px] text-gray-600">18h00</div>
                  <div>
                    <strong>Départ</strong>
                    <p className="text-gray-700">Route vers La Rochelle (30 min) pour train retour ou route. La tête pleine de beaux souvenirs !</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Option 3 jours */}
        <section className="mb-16 bg-green-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">➕ Si Vous Restez 3 Jours (Lundi)</h2>
          <p className="text-gray-700 mb-4">
            Un jour supplémentaire vous permet de ralentir le rythme et d'ajouter :
          </p>
          <ul className="space-y-2 text-gray-700">
            <li>✓ <strong>Matinée spa & farniente</strong> à la villa (profiter du jardin, lecture, repos)</li>
            <li>✓ <strong>Village de La Flotte</strong> (Plus Beau Village de France) et Abbaye des Châteliers</li>
            <li>✓ <strong>Dégustation huîtres</strong> dans une cabane ostréicole au port</li>
            <li>✓ <strong>Sports nautiques</strong> : initiation paddle ou kayak (2h)</li>
            <li>✓ <strong>Exploration à vélo</strong> des petites routes secrètes de l'île</li>
          </ul>
        </section>

        {/* Conseils pratiques */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">💡 Nos Conseils pour un Week-end Réussi</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-3">📅 Meilleure période</h3>
              <p className="text-gray-700">
                <strong>Avril-Juin & Sept-Oct :</strong> Temps agréable, peu de monde, tarifs doux. Parfait pour week-end !<br/>
                <strong>Juillet-Août :</strong> Très animé, réservation 6 mois à l'avance nécessaire.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-3">🚗 Voiture ou pas ?</h3>
              <p className="text-gray-700">
                <strong>Avec voiture :</strong> Plus de liberté, surtout si arrivée tardive.<br/>
                <strong>Sans voiture :</strong> Faisable ! Vélo électrique + bus. L'île est petite (30 km de long).
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-3">🧳 À ne pas oublier</h3>
              <p className="text-gray-700">
                Maillot de bain, casquette, crème solaire, chaussures de marche confortables, 
                coupe-vent (brise marine), appareil photo !
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-3">💰 Budget indicatif</h3>
              <p className="text-gray-700">
                <strong>Transport :</strong> 100-150€ (TGV A/R)<br/>
                <strong>Hébergement :</strong> Variable (voir ci-dessous)<br/>
                <strong>Sur place :</strong> 100-150€ (vélo, repas, visites)
              </p>
            </div>
          </div>
        </section>

        {/* Villa courts séjours */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">🏡 Villa Nomade - Courts Séjours Possibles</h2>
          <div className="bg-gradient-to-br from-amber-100 to-orange-100 p-8 rounded-lg">
            <div className="flex items-start gap-4 mb-6">
              <MapPin className="w-12 h-12 text-[#c17a3a] flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-3">Week-ends & Courts Séjours (Hors Saison)</h3>
                <p className="text-gray-700 mb-4">
                  Notre villa de luxe accepte les <strong>courts séjours de 2-3 nuits hors saison</strong> (octobre à mai). 
                  Profitez de tout le confort d'une villa haut de gamme pour votre escapade : 
                  spa de nage chauffé, jardin paysager, 4 chambres, emplacement idéal.
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li>✓ <strong>Capacité :</strong> 2 à 8 personnes (idéal couples ou petits groupes)</li>
                  <li>✓ <strong>Tarif week-end :</strong> Nous consulter (tarif dégressif)</li>
                  <li>✓ <strong>Minimum :</strong> 2 nuits</li>
                  <li>✓ <strong>Disponibilité :</strong> Octobre à Mai uniquement</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <Heart className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Réservez Votre Week-end Évasion</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Villa de luxe • Spa de nage • Emplacement idéal<br/>
            Courts séjours possibles hors saison
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Demander disponibilités week-end
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Découvrir la villa
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          "name": "Week-end Île de Ré",
          "description": "Programme optimisé pour un week-end de 2-3 jours à l'Île de Ré depuis Paris",
          "touristType": "Week-end, court séjour",
          "itinerary": {
            "@type": "ItemList",
            "numberOfItems": 2
          }
        })}
      </script>
    </>
  );
};

export default WeekendIleDeRe;